/**
 * Brand guardrail PRIMITIVES shared by client + server — ONE source of truth for the word lists.
 * sanitize, injection/hostility/profanity detection, a single-block textFlagReason, and
 * screenCustomChip() for pre-checking member-typed "add your own" chips on the client.
 *
 * The server builds the full output validation (cleanReview / stripUrls / hasViolation /
 * BANNED_PHRASES) on top of these in server/src/services/guardrails.ts. Do NOT duplicate these
 * lists anywhere — import from here.
 * @see docs/reference/pennysaver_main.py
 */

export const INJECTION_PATTERNS =
  /(ignore (all |previous |your )?instructions|system\s*:|assistant\s*:|\bjailbreak\b|pretend to be|disregard (the|your) |you are now|act as)/i;

export const HOSTILE_PATTERNS =
  /(\bkill\b|\bhate\b|\bdisgusting\b|\bworst\b|\bripoff\b|\brip[- ]off\b|\bscam\b|\bawful\b|\bterrible\b)/i;

export function sanitize(text: string | undefined | null): string {
  if (!text) return '';
  return text.replace(/`/g, "'").replace(/@everyone/g, 'everyone').replace(/@here/g, 'here').trim();
}

export function looksLikeInjection(t: string | undefined | null): boolean {
  return INJECTION_PATTERNS.test(t ?? '');
}

/**
 * Common profanity + slurs. The brand is positivity-only, so member-typed text containing any of
 * these is rejected (custom chips) or held for a mod (drafts), never auto-posted. Bare ambiguous
 * tokens like "dick" are intentionally omitted to avoid flagging legitimate proper nouns.
 */
export const PROFANITY: string[] = [
  'fuck', 'fucking', 'shit', 'shitty', 'bitch', 'bastard', 'asshole', 'dickhead', 'piss',
  'pissed', 'cunt', 'slut', 'whore', 'prick', 'douchebag', 'twat', 'wanker', 'bollocks',
  'jackass', 'motherfucker', 'bullshit',
  // slurs — blocked outright
  'nigger', 'faggot', 'retard', 'spic', 'chink', 'kike', 'tranny',
];

const PROFANITY_RE = new RegExp(`\\b(${PROFANITY.join('|')})\\b`, 'i');

/** True when the text contains profanity or a slur from the PROFANITY list. */
export function hasProfanity(text: string | undefined | null): boolean {
  return PROFANITY_RE.test(text ?? '');
}

/**
 * Hostility / injection / profanity check for a single block of TEXT (e.g. a member-edited draft
 * or a custom chip). Returns a mod-queue/rejection reason, or null when acceptable. Positivity
 * only: negative or profane text must never auto-post.
 */
export function textFlagReason(text: string | undefined | null): string | null {
  const t = text ?? '';
  if (looksLikeInjection(t)) return 'Possible prompt injection';
  if (HOSTILE_PATTERNS.test(t)) return 'Possible personal attack / hostile tone';
  if (hasProfanity(t)) return 'Profanity or inappropriate language';
  return null;
}

export type CustomChipResult = { ok: true; value: string } | { ok: false; message: string };

/**
 * Validate a member-typed "add your own" chip BEFORE it is accepted. Sanitizes, length-limits, and
 * runs the same injection/hostility/profanity checks the server enforces. Returns the clean value
 * or a gentle inline message. Positivity-only — keep it kind and on the food.
 */
export function screenCustomChip(raw: string): CustomChipResult {
  const value = sanitize(raw);
  if (!value) return { ok: false, message: 'Type a few words first.' };
  if (value.length > 40) return { ok: false, message: 'Keep it under 40 characters.' };
  if (textFlagReason(value)) {
    return { ok: false, message: 'Let’s keep it kind and about the food. Try rephrasing.' };
  }
  return { ok: true, value };
}
